/**
 * Permission Utility Functions
 * Provides role-based access control helpers
 */

/**
 * Check if user has specific permission for a feature
 * @param {Array} userRoles - Array of user role assignments with permissions
 * @param {string} feature - Feature name (e.g., 'accounts', 'users', 'billing')
 * @param {string} requiredLevel - Required access level ('read', 'write', 'delete', 'admin')
 * @returns {boolean}
 */
export const hasPermission = (userRoles, feature, requiredLevel = 'read') => {
  if (!userRoles || userRoles?.length === 0) return false;

  // Super Admins bypass all permission checks - they have access to everything
  if (isSuperAdmin(userRoles)) return true;

  const accessLevelHierarchy = {
    none: 0,
    read: 1,
    write: 2,
    delete: 3,
    admin: 4
  };

  const requiredLevelValue = accessLevelHierarchy?.[requiredLevel] || 0;

  // Check if any of user's roles has sufficient permission
  return userRoles?.some(roleAssignment => {
    const role = roleAssignment?.role;
    if (!role?.permissions) return false;

    const permission = role?.permissions?.find(p => p?.feature === feature);
    if (!permission) return false;

    const userLevelValue = accessLevelHierarchy?.[permission?.accessLevel] || 0;
    return userLevelValue >= requiredLevelValue;
  });
};

/**
 * Check if user has any of the specified roles
 * @param {Array} userRoles - Array of user role assignments
 * @param {Array<string>} roleNames - Array of role names to check
 * @returns {boolean}
 */
export const hasAnyRole = (userRoles, roleNames) => {
  if (!userRoles || userRoles?.length === 0) return false;
  if (!roleNames || roleNames?.length === 0) return false;

  return userRoles?.some(roleAssignment => 
    roleNames?.includes(roleAssignment?.role?.name)
  );
};

/**
 * Check if user is Super Admin
 * @param {Array} userRoles - Array of user role assignments
 * @returns {boolean}
 */
export const isSuperAdmin = (userRoles) => {
  if (!userRoles || userRoles?.length === 0) return false;
  
  return userRoles?.some(roleAssignment => 
    roleAssignment?.role?.hierarchy_level >= 100
  );
};

/**
 * Check if user is Business Administrator
 * @param {Array} userRoles - Array of user role assignments
 * @param {string} tenantId - Optional tenant ID to check for specific tenant
 * @returns {boolean}
 */
export const isBusinessAdmin = (userRoles, tenantId = null) => {
  if (!userRoles || userRoles?.length === 0) return false;
  
  // Super Admins have access to all organization admin features
  if (isSuperAdmin(userRoles)) return true;
  
  return userRoles?.some(roleAssignment => {
    const isAdmin = roleAssignment?.role?.hierarchy_level >= 80;
    if (!tenantId) return isAdmin;
    return isAdmin && roleAssignment?.tenant_id === tenantId;
  });
};

/**
 * Check if user can manage other users
 * @param {Array} userRoles - Array of user role assignments
 * @returns {boolean}
 */
export const canManageUsers = (userRoles) => {
  return hasPermission(userRoles, 'users', 'write') || isSuperAdmin(userRoles);
};

/**
 * Check if user can invite to organization
 * @param {Array} userRoles - Array of user role assignments
 * @param {string} tenantId - Tenant ID to check invitation permission for
 * @returns {boolean}
 */
export const canInviteToOrganization = (userRoles, tenantId) => {
  return isSuperAdmin(userRoles) || isBusinessAdmin(userRoles, tenantId);
};

/**
 * Get highest hierarchy level from user roles
 * @param {Array} userRoles - Array of user role assignments
 * @returns {number}
 */
export const getHighestHierarchyLevel = (userRoles) => {
  if (!userRoles || userRoles?.length === 0) return 0;
  
  return Math.max(...userRoles?.map(ra => ra?.role?.hierarchy_level || 0));
};

/**
 * Check if user can access tenant data
 * @param {string} userTenantId - User's tenant ID
 * @param {string} resourceTenantId - Resource's tenant ID
 * @param {Array} userRoles - Array of user role assignments
 * @returns {boolean}
 */
export const canAccessTenantData = (userTenantId, resourceTenantId, userRoles) => {
  // Super admins can access all tenant data - no restrictions
  if (isSuperAdmin(userRoles)) return true;
  
  // Users can only access their own tenant's data
  return userTenantId === resourceTenantId;
};

/**
 * Filter data by tenant access
 * @param {Array} data - Array of data items with tenant_id
 * @param {string} userTenantId - User's tenant ID
 * @param {Array} userRoles - Array of user role assignments
 * @returns {Array}
 */
export const filterByTenantAccess = (data, userTenantId, userRoles) => {
  if (!data) return [];
  
  // Super admins see all data - no filtering whatsoever
  if (isSuperAdmin(userRoles)) return data;
  
  // Filter to user's tenant only
  return data?.filter(item => item?.tenantId === userTenantId || item?.tenant_id === userTenantId);
};